"use client";

// Богатый текстовый редактор «Базы знаний» (TipTap/ProseMirror — как в Weeek).
// Картинки грузятся на бэк (/api/kb/files) и рендерятся через AuthImage (Bearer→blob).

import { useRef } from "react";
import { useEditor, EditorContent, ReactNodeViewRenderer, NodeViewWrapper, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, Underline as UnderlineIcon, Strikethrough, Heading1, Heading2, Heading3, List, ListOrdered, ListChecks, ImageIcon, Quote, Code, Highlighter, AlignLeft, AlignCenter, AlignRight, Undo2, Redo2, Type } from "lucide-react";
import { IconLink } from "@/components/ui/icons";

import { api } from "@/lib/api";
import { AuthImage } from "@/components/ui/auth-image";

// Картинка-нода с авторизованной загрузкой (приватная отдача бэка).
function ImgNodeView({ node }: { node: { attrs: { src?: string; alt?: string } } }) {
  return (
    <NodeViewWrapper className="kb-doc-img my-2" data-drag-handle>
      <AuthImage src={node.attrs.src} alt={node.attrs.alt} className="max-w-full rounded-[var(--radius-md)] border border-[var(--border)]" />
    </NodeViewWrapper>
  );
}
const AuthImageExt = Image.extend({
  addNodeView() { return ReactNodeViewRenderer(ImgNodeView); },
});

const TEXT_COLORS = ["#ef4444", "#f59e0b", "#10b981", "#0ea5e9", "#8b5cf6", "#ec4899"];

function Btn({ on, onClick, title, children }: { on?: boolean; onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} data-tooltip={title}
      style={{ boxShadow: "none" }}
      className={`grid h-7 w-7 place-items-center rounded-[var(--radius-md)] text-[var(--textMuted)] transition hover:bg-[var(--hover)] hover:text-[var(--text)] ${on ? "bg-[var(--secondary)] text-[var(--primary)]" : ""}`}>
      {children}
    </button>
  );
}
const Sep = () => <span className="mx-0.5 h-5 w-px bg-[var(--border)]" />;

function Toolbar({ editor }: { editor: Editor }) {
  const fileRef = useRef<HTMLInputElement>(null);
  async function pickImage(files: FileList | null) {
    if (!files?.length) return;
    for (const f of Array.from(files)) {
      try { const r = await api.kbUploadFile(f); editor.chain().focus().setImage({ src: r.url }).run(); } catch { /* тихо */ }
    }
    if (fileRef.current) fileRef.current.value = "";
  }
  function toggleLink() {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Ссылка:", prev || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }
  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 border-b border-[var(--border)] bg-[var(--surface)] p-1.5">
      <Btn title="Отменить" onClick={() => editor.chain().focus().undo().run()}><Undo2 size={14} /></Btn>
      <Btn title="Повторить" onClick={() => editor.chain().focus().redo().run()}><Redo2 size={14} /></Btn>
      <Sep />
      <Btn title="Заголовок 1" on={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><Heading1 size={15} /></Btn>
      <Btn title="Заголовок 2" on={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={15} /></Btn>
      <Btn title="Заголовок 3" on={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={15} /></Btn>
      <Btn title="Обычный текст" on={editor.isActive("paragraph")} onClick={() => editor.chain().focus().setParagraph().run()}><Type size={14} /></Btn>
      <Sep />
      <Btn title="Жирный" on={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={14} /></Btn>
      <Btn title="Курсив" on={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={14} /></Btn>
      <Btn title="Подчёркнутый" on={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon size={14} /></Btn>
      <Btn title="Зачёркнутый" on={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough size={14} /></Btn>
      <Btn title="Выделение" on={editor.isActive("highlight")} onClick={() => editor.chain().focus().toggleHighlight().run()}><Highlighter size={14} /></Btn>
      <span className="mx-0.5 flex items-center gap-0.5">
        {TEXT_COLORS.map((c) => (
          <button key={c} type="button" data-tooltip="Цвет текста" onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().setColor(c).run()} style={{ background: c, boxShadow: "none" }}
            className="h-4 w-4 rounded-full border border-black/10" />
        ))}
        <button type="button" data-tooltip="Сбросить цвет" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().unsetColor().run()}
          style={{ boxShadow: "none" }} className="grid h-4 w-4 place-items-center rounded-full border border-[var(--border)] text-[9px] text-[var(--textMuted)]">×</button>
      </span>
      <Sep />
      <Btn title="Список" on={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={14} /></Btn>
      <Btn title="Нумерованный список" on={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={14} /></Btn>
      <Btn title="Чек-лист" on={editor.isActive("taskList")} onClick={() => editor.chain().focus().toggleTaskList().run()}><ListChecks size={14} /></Btn>
      <Sep />
      <Btn title="Цитата" on={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={14} /></Btn>
      <Btn title="Код" on={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()}><Code size={14} /></Btn>
      <Btn title="Ссылка" on={editor.isActive("link")} onClick={toggleLink}><IconLink size={14} /></Btn>
      <Btn title="Картинка" onClick={() => fileRef.current?.click()}><ImageIcon size={14} /></Btn>
      <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => void pickImage(e.target.files)} />
      <Sep />
      <Btn title="По левому краю" on={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft size={14} /></Btn>
      <Btn title="По центру" on={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter size={14} /></Btn>
      <Btn title="По правому краю" on={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight size={14} /></Btn>
    </div>
  );
}

export function RichEditor({ value, onChange, editable = true }: {
  value: string; onChange?: (html: string) => void; editable?: boolean;
}) {
  const editor = useEditor({
    editable,
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextStyle,
      Color,
      Highlight,
      TaskList,
      TaskItem.configure({ nested: true }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noreferrer", target: "_blank" } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder: "Начните писать документ…" }),
      AuthImageExt,
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
  }, []);

  if (!editor) return null;
  return (
    <div className="flex h-full min-h-0 flex-col">
      {editable && <Toolbar editor={editor} />}
      <EditorContent editor={editor} className="kb-prose min-h-0 flex-1 overflow-y-auto scrollbar-autohide px-5 py-4" />
    </div>
  );
}
